/**
 * Notifications inbox — filtered list (All / Alerts / Buddies).
 * Lives inside Profile Hub shell (Phase 6a-5 enamel).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaMapMarkerAlt, FaTimes, FaToolbox } from 'react-icons/fa';
import { GiCorn, GiDeerTrack } from 'react-icons/gi';
import { notificationsService } from '../../../services/photoEngagementService';
import { ProfileHubShell } from '../../profile/components/ProfileHubShell';
import { NOTIFICATION_FILTERS, USDI_YONA_USERNAME } from '../constants';
import {
  formatNotificationRelative,
  getNotificationActorLabel,
  getNotificationDestination,
  getNotificationSummary,
  isAlertNotification,
} from '../notificationUtils';
import {
  notifyNotificationsChanged,
  useNotificationsUnread,
} from '../hooks/useNotificationsUnread';
import {
  fetchNotificationsList,
  getCachedNotifications,
  invalidateNotificationsCache,
  setCachedNotifications,
} from '../../profile/utils/accountHubCache';
import { ClubMemberInvitesInbox } from '../../clubs/components/ClubMemberInvitesInbox';
import { mapPrintService } from '../../toolkit/services/mapPrintService';
import { downloadBlob } from '../../../utils/downloadBlob';
import { useToast } from '../../../components/ui/ToastProvider';
import './notifications-shell.css';

const YONA_BRAND_MARK_SRC = `${process.env.PUBLIC_URL || ''}/brand/yona-bear-2.png`;

function NotificationIcon({ type }) {
  if (type === 'feeder_low' || type === 'club_feed_filled' || type === 'club_feed_low') return <GiCorn aria-hidden />;
  if (type === 'hunt_sign_out' || type === 'hunt_sign_in') return <FaMapMarkerAlt aria-hidden />;
  if (type === 'photo_posted' || type === 'photo_comment') return <GiDeerTrack aria-hidden />;
  if (isAlertNotification(type)) return <FaToolbox aria-hidden />;
  return <FaBell aria-hidden />;
}

function formatKindLabel(type) {
  if (!type) return 'UPDATE';
  return String(type).replace(/_/g, ' ').toUpperCase();
}

function NotificationListItem({ item, onNavigate, onDismiss, dismissingId }) {
  const isSystem = item?.actor?.username === USDI_YONA_USERNAME;
  const isAlert = isAlertNotification(item?.type);
  const actorLabel = getNotificationActorLabel(item, USDI_YONA_USERNAME);
  const summary = getNotificationSummary(item);
  const destination = getNotificationDestination(item);
  const isDismissing = dismissingId === item.id;
  const isUnread = !item.read_at;

  const handleOpen = () => {
    if (!destination) return;
    if (destination.downloadPrintJobId) {
      onNavigate(destination);
      return;
    }
    if (typeof destination === 'string') {
      onNavigate(destination);
      return;
    }
    onNavigate(
      {
        pathname: destination.pathname,
        search: destination.search || '',
      },
      { state: destination.state }
    );
  };

  const iconClass = [
    'notif-item__icon',
    isSystem ? 'notif-item__icon--system' : '',
    !isSystem && isAlert ? 'notif-item__icon--alert' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`notif-item${isUnread ? ' notif-item--unread' : ''}`}
      data-testid={`notif-item-${item.id}`}
    >
      <button type="button" className="notif-item__main" onClick={handleOpen} disabled={!destination}>
        <span className={iconClass} aria-hidden="true">
          {isSystem ? (
            <img src={YONA_BRAND_MARK_SRC} alt="" className="notif-item__bear" />
          ) : (
            <NotificationIcon type={item.type} />
          )}
        </span>
        <span className="notif-item__body">
          <span className="notif-item__actor">{actorLabel}</span>
          <span className="notif-item__summary">{summary}</span>
          {item.metadata?.is_final_reminder ? (
            <span className="notif-item__tag">Final reminder</span>
          ) : null}
          <span className="notif-item__time">
            {formatKindLabel(item.type)} · {formatNotificationRelative(item.created_at)}
          </span>
        </span>
        {isUnread ? (
          <span className="notif-item__unread-dot" data-testid={`notif-unread-dot-${item.id}`} aria-hidden="true" />
        ) : null}
      </button>
      <button
        type="button"
        className="notif-item__dismiss"
        aria-label="Dismiss notification"
        disabled={isDismissing}
        onClick={() => onDismiss(item.id)}
      >
        <FaTimes aria-hidden />
      </button>
    </div>
  );
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const { unreadTotal: unreadNotificationsTotal } = useNotificationsUnread();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState(() => getCachedNotifications('all')?.results || []);
  const [count, setCount] = useState(() => getCachedNotifications('all')?.count || 0);
  const [loading, setLoading] = useState(() => !getCachedNotifications('all'));
  const [dismissingId, setDismissingId] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async ({ force = false } = {}) => {
    const cached = !force ? getCachedNotifications(filter) : null;
    if (cached) {
      setItems(cached.results || []);
      setCount(Number(cached.count) || 0);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchNotificationsList(
        filter,
        () => notificationsService.list(1, filter),
        { force },
      );
      setItems(data?.results || []);
      setCount(Number(data?.count) || 0);
    } catch (e) {
      setItems([]);
      setCount(0);
      setError('Unable to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDismiss = async (notificationId) => {
    setDismissingId(notificationId);
    try {
      await notificationsService.dismiss(notificationId);
      setItems((prev) => {
        const next = prev.filter((n) => n.id !== notificationId);
        const nextCount = Math.max(0, next.length);
        invalidateNotificationsCache();
        setCachedNotifications(filter, { results: next, count: nextCount });
        return next;
      });
      setCount((prev) => Math.max(0, prev - 1));
      notifyNotificationsChanged();
    } catch (e) {
      setError('Could not dismiss that notification.');
    } finally {
      setDismissingId(null);
    }
  };

  const handleClearAll = async () => {
    if (clearingAll || loading || items.length === 0) return;
    setClearingAll(true);
    setError('');
    try {
      await notificationsService.dismissAll(filter);
      setItems([]);
      setCount(0);
      invalidateNotificationsCache();
      setCachedNotifications(filter, { results: [], count: 0 });
      notifyNotificationsChanged();
    } catch (e) {
      setError('Could not clear notifications.');
    } finally {
      setClearingAll(false);
    }
  };

  const handleOpenDestination = useCallback(async (destination, navState) => {
    if (destination?.downloadPrintJobId) {
      try {
        const { blob, filename } = await mapPrintService.downloadJob(destination.downloadPrintJobId);
        downloadBlob(blob, filename);
      } catch (e) {
        try {
          showToast('Could not download the map. Please try again.', 'error');
        } catch (_) { /* toast optional in tests */ }
      }
      return;
    }
    if (typeof destination === 'string') {
      navigate(destination);
      return;
    }
    navigate(destination, navState);
  }, [navigate, showToast]);

  const canClearAll = !loading && !clearingAll && items.length > 0;

  return (
    <ProfileHubShell
      activeId="notifications"
      unreadNotificationsTotal={unreadNotificationsTotal}
    >
      <div className="notif-shell" data-testid="notifications-page">
        <div className="notif-shell__toolbar">
          <div className="notif-shell__filters" role="tablist" aria-label="Notification filters">
            {NOTIFICATION_FILTERS.map((tab) => {
              const active = filter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`notif-shell__filter${active ? ' notif-shell__filter--active' : ''}`}
                  onClick={() => setFilter(tab.id)}
                >
                  {tab.label}
                  {active && count > 0 ? (
                    <span className="notif-shell__count-badge" data-testid="notif-count-badge">
                      {count > 99 ? '99+' : count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="notif-shell__clear-all"
            data-testid="notif-clear-all"
            disabled={!canClearAll}
            onClick={handleClearAll}
          >
            {clearingAll ? 'CLEARING…' : 'CLEAR ALL'}
          </button>
        </div>

        <ClubMemberInvitesInbox />

        <div className="notif-shell__body">
          {loading ? (
            <p className="notif-shell__loading" data-testid="notif-loading">
              Loading…
            </p>
          ) : error ? (
            <p className="notif-shell__empty">{error}</p>
          ) : items.length === 0 ? (
            <p className="notif-shell__empty" data-testid="notif-empty">
              {filter === 'alerts'
                ? 'No alerts right now.'
                : filter === 'buddies'
                  ? 'No buddy activity right now.'
                  : 'You are all caught up.'}
            </p>
          ) : (
            <div className="notif-shell__list">
              {items.map((item) => (
                <NotificationListItem
                  key={item.id}
                  item={item}
                  onNavigate={handleOpenDestination}
                  onDismiss={handleDismiss}
                  dismissingId={dismissingId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ProfileHubShell>
  );
}

export default NotificationsPage;
